import React from 'react';
import { GitBranch, CheckCircle2, Circle } from 'lucide-react';
import { getRoadmap, getCurrentStepIndex } from '../../config/roadmaps';
import { getProgramByServiceType } from '../../config/programs';
import { SERVICE_TYPE_LABELS } from '../../services/constants';

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; accent: string; badge: string; check: string; dot: string; dotBg: string; line: string }> = {
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', accent: 'text-purple-600', badge: 'bg-purple-200 text-purple-700', check: 'text-purple-500', dot: 'bg-purple-500', dotBg: 'bg-purple-100 border-purple-500', line: 'border-purple-200' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', accent: 'text-blue-600', badge: 'bg-blue-200 text-blue-700', check: 'text-blue-500', dot: 'bg-blue-500', dotBg: 'bg-blue-100 border-blue-500', line: 'border-blue-200' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', accent: 'text-amber-600', badge: 'bg-amber-200 text-amber-700', check: 'text-amber-500', dot: 'bg-amber-500', dotBg: 'bg-amber-100 border-amber-500', line: 'border-amber-200' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', accent: 'text-green-600', badge: 'bg-green-200 text-green-700', check: 'text-green-500', dot: 'bg-green-500', dotBg: 'bg-green-100 border-green-500', line: 'border-green-200' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', accent: 'text-red-600', badge: 'bg-red-200 text-red-700', check: 'text-red-500', dot: 'bg-red-500', dotBg: 'bg-red-100 border-red-500', line: 'border-red-200' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800', accent: 'text-violet-600', badge: 'bg-violet-200 text-violet-700', check: 'text-violet-500', dot: 'bg-violet-500', dotBg: 'bg-violet-100 border-violet-500', line: 'border-violet-200' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800', accent: 'text-teal-600', badge: 'bg-teal-200 text-teal-700', check: 'text-teal-500', dot: 'bg-teal-500', dotBg: 'bg-teal-100 border-teal-500', line: 'border-teal-200' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', accent: 'text-orange-600', badge: 'bg-orange-200 text-orange-700', check: 'text-orange-500', dot: 'bg-orange-500', dotBg: 'bg-orange-100 border-orange-500', line: 'border-orange-200' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', accent: 'text-rose-600', badge: 'bg-rose-200 text-rose-700', check: 'text-rose-500', dot: 'bg-rose-500', dotBg: 'bg-rose-100 border-rose-500', line: 'border-rose-200' },
};
const DEFAULT_COLORS = COLOR_MAP.purple;

interface Props {
  application: Record<string, any>;
}

export const ProjectTimelineWidget: React.FC<Props> = ({ application: app }) => {
  const serviceType = app.service_type || 'pup_grant';
  const roadmap = getRoadmap(serviceType);
  if (!roadmap) return null;

  const prog = getProgramByServiceType(serviceType);
  const statusField = prog?.kanbanStatusField || 'status';
  const currentStatus = app[statusField] || app.status || roadmap.steps[0]?.id || '';

  const mainSteps = roadmap.steps.filter(s =>
    !roadmap.terminalStatuses.includes(s.id) || s.id === currentStatus
  );
  const currentIdx = getCurrentStepIndex(serviceType, currentStatus);
  const activeIdx = currentIdx >= 0 ? currentIdx : 0;

  const productLabel = SERVICE_TYPE_LABELS[serviceType] || serviceType;
  const c = COLOR_MAP[prog?.color || 'purple'] || DEFAULT_COLORS;

  return (
    <div className={`bg-gradient-to-br ${c.bg} rounded-lg shadow p-5 border ${c.border}`}>
      <h3 className={`font-bold text-sm flex items-center gap-2 mb-4 ${c.text}`}>
        <GitBranch size={16} />
        Timeline — {productLabel}
      </h3>
      <div className="space-y-2">
        {mainSteps.map((stage, i) => {
          const done = i < activeIdx;
          const current = i === activeIdx;
          return (
            <div key={stage.id} className="flex items-center gap-3">
              {done ? (
                <CheckCircle2 size={18} className={`${c.check} shrink-0`} />
              ) : current ? (
                <div className={`w-[18px] h-[18px] rounded-full border-2 ${c.dotBg} shrink-0 flex items-center justify-center`}>
                  <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                </div>
              ) : (
                <Circle size={18} className="text-gray-300 shrink-0" />
              )}
              <span className={`text-sm ${done ? `${c.accent} line-through` : current ? `${c.text} font-semibold` : 'text-gray-400'}`}>
                {stage.label}
              </span>
              {current && <span className={`text-xs ${c.badge} px-2 py-0.5 rounded-full ml-auto`}>Aktualny</span>}
            </div>
          );
        })}
      </div>
      {app.requested_amount && (
        <div className={`mt-3 pt-3 border-t ${c.line} text-sm text-gray-600`}>
          Wartość projektu: <strong>{Number(app.requested_amount).toLocaleString('pl-PL')} zł</strong>
        </div>
      )}
    </div>
  );
};
